'use client';
import { Spinner } from '@/app/components/Spinner';
import {
  currentAccountIdAtom,
  currentAccountUsersAtom,
  currentAccountUserFlagsAtom,
  currentUserAtom,
} from '@/app/states/atoms';
import { fetchCurrentUser, fetchFlagsFromAPI } from '@/utils/userAccount.util';
import { useAtom } from 'jotai';
import { usePathname } from 'next/navigation';
import { ReactNode, useEffect, useState, useRef, useCallback } from 'react';

interface AuthLayoutProps {
  children: ReactNode;
}

export default function AuthLayout({ children }: AuthLayoutProps) {
  const [loading, setLoading] = useState(true);
  const [, setCurrentUser] = useAtom(currentUserAtom);
  const [, setCurrentAccountUsers] = useAtom(currentAccountUsersAtom);
  const [, setCurrentAccountUserFlags] = useAtom(currentAccountUserFlagsAtom);
  const [currentAccountId, setCurrentAccountId] = useAtom(currentAccountIdAtom);
  const pathname = usePathname();
  const isInitialLoad = useRef(true);

  const loadFlags = useCallback(async () => {
    try {
      const flagsData = await fetchFlagsFromAPI();
      const flagsArray = flagsData.accountUsers.map(au => ({
        accountUserId: au.id,
        flags: au.flags || [],
      }));
      setCurrentAccountUserFlags(flagsArray);
    } catch (error) {
      console.error('Failed to fetch user flags:', error);
    }
  }, [setCurrentAccountUserFlags]);

  useEffect(() => {
    const fetchUser = async () => {
      const userPayload = await fetchCurrentUser();
      setCurrentUser(userPayload?.user);
      setCurrentAccountUsers(userPayload?.accountUsers);
      const isValidAccountId = userPayload?.accountUsers.some(
        accountUser => accountUser.accountId === currentAccountId,
      );
      if (!currentAccountId || !isValidAccountId) {
        const newAccountId = userPayload?.accountUsers[0]?.accountId;
        if (newAccountId) setCurrentAccountId(newAccountId);
      }

      await loadFlags();
      setLoading(false);
    };
    fetchUser();
  }, [currentAccountId, setCurrentAccountId, setCurrentAccountUsers, setCurrentUser, loadFlags]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    loadFlags();
  }, [pathname, loadFlags]);

  if (loading) {
    return (
      <div className="flex h-screen">
        <Spinner className="m-auto size-8" />
      </div>
    );
  }

  return <>{children}</>;
}
